import { useCallback, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import { useDispatch, useSelector } from 'react-redux'
import { updateFilter, showFilter } from '../../../../../store/actions/filter.actions'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import isEmpty from 'lodash/isEmpty'
import { updateSkip } from '../../../../../store/actions/datatable.actions'
import DialogConfirmCancel from '../../../../dialog/DialogConfirm'
import InputField from '../../../../FormControl/input/InputField'
import { Typography } from '@mui/material'
import { SelectMultipleFieldYears } from '../../../../FormControl/SelectRequest/SelectMultipleFieldYears'
import { handlerErrorV2 } from '../../../../../utils/errors'
import FormCurriculumElements from './FormCurriculumElements'
import { getSubjectsFromClasses } from '../../../../../services/subjects/subjects.service'
import AutocompleteField from '../../../../FormControl/input/AutocompleteField'

const FilterDialog = ({ module }) => {
  const { curriculumId = 'assigned' } = useParams()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const years = useSelector((state) => state?.tenant?.years || [])
  const filter = useSelector((state) => state?.filter?.[module]?.filter || {})
  const teachers = useSelector((state) => state?.class?.[curriculumId]?.teachers || [])
  const curriculumList = useSelector((state) => state?.assessment?.curriculum || [])

  const open = useSelector((state) => state?.filter?.[module]?.open || false)

  const dispatch = useDispatch()

  const [form, setInputForm] = useState(filter)
  const [inputErrors, setInputErrors] = useState({})
  const [subjects, setSubjects] = useState([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    if (!isEmpty(filter)) {
      setInputForm(filter)
    }
  }, [filter])

  const onClose = () => {
    dispatch(showFilter(module, false))
  }
  const onChange = (key, value) => {
    if (key === 'yearLevel') {
      const yearLevel_name = years
        .filter((year) => value.includes(year._id))
        .map((year) => ({ _id: year._id, name: year.name }))
      setInputForm({ ...form, [key]: value, yearLevel_name })
    } else if (key === 'teachers') {
      const teachersId = value?.map((item) => item.value)
      setInputForm({ ...form, [key]: value, teachersId })
    } else if (key === 'subjectId') {
      setInputForm({ ...form, subject: value, [key]: value.value, subjectId_name: value.label })
    } else {
      setInputForm({ ...form, [key]: value })
    }
  }

  const onApply = () => {
    onClose()
    const filter = {
      ...form
    }
    if (isEmpty(filter.yearLevel)) {
      delete filter.yearLevel
      delete filter.yearLevel_name
    }
    if (isEmpty(filter.teachers)) {
      delete filter.teachers
      delete filter.teachersId
    }
    if (!filter.subjectId) {
      delete filter.subject
      delete filter.subjectId
      delete filter.subjectId_name
    }
    dispatch(updateFilter(module, filter))
    dispatch(updateSkip(module, 0))
    navigate(pathname, { state: filter })
  }

  const onLoad = useCallback(async () => {
    try {
      setIsLoading(true)
      const response = await getSubjectsFromClasses(curriculumId)
      if (!response?.data && response?.error) {
        const message = response.error.message || 'An error ocurred'
        setInputErrors({ message })
      } else {
        const subjectsData =
          response.data?.map((item) => {
            const curriculum = curriculumList.find((curr) => curr._id === item.curriculumId)
            // item.curriculumId is the curriculum of the CLASS using this
            // subject; classes without a curriculum group under "Unassigned".
            const groupLabel = item.curriculumId
              ? curriculum?.name || item.curriculumId
              : 'Unassigned'
            return {
              value: item._id,
              label: item.name,
              curriculumId: groupLabel
            }
          }) || []
        setSubjects(
          subjectsData.sort((a, b) => {
            if (a.curriculumId < b.curriculumId) return -1
            if (a.curriculumId > b.curriculumId) return 1
            return 0
          })
        )
        const find = response.data.find((item) => item._id === form.subjectId)
        if (!find) {
          onChange('subjectId', '')
        }
      }
    } catch (error) {
      const message = handlerErrorV2(error)
      setInputErrors({ message })
    } finally {
      setIsLoading(false)
    }
    // eslint-disable-next-line
  }, [curriculumId])

  useEffect(() => {
    onLoad()
  }, [onLoad])

  return (
    <DialogConfirmCancel
      id='dr-filter'
      open={open}
      title='Filter'
      maxWidth='xs'
      onCancel={onClose}
      onConfirm={onApply}
      textConfirm='Apply'
    >
      <Box className='grid grid-cols-4 gap-4'>
        <Typography
          variant='inherit'
          className='font-bold capitalize'
          color='primary'
          align='right'
          component='p'
        >
          Code
        </Typography>
        <div className='col-span-3'>
          <InputField
            id='code'
            name='code'
            value={form.code}
            onChange={(e) => onChange('code', e.target.value)}
          />
        </div>
        {module !== 'surveyClassList' && (
          <>
            <Typography
              variant='inherit'
              className='font-bold capitalize'
              color='primary'
              align='right'
              component='p'
            >
              Name
            </Typography>
            <div className='col-span-3'>
              <InputField
                id='name'
                name='name'
                value={form.name}
                onChange={(e) => onChange('name', e.target.value)}
              />
            </div>
          </>
        )}
        <Typography
          variant='inherit'
          className='font-bold capitalize'
          color='primary'
          align='right'
          component='p'
        >
          Year Level
        </Typography>
        <div className='col-span-3'>
          <SelectMultipleFieldYears
            id='yearLevel'
            name='yearLevel'
            value={form.yearLevel}
            onChange={(e) => onChange('yearLevel', e.target.value)}
          />
        </div>
        <FormCurriculumElements formData={form?.curriculumElements || {}} onChangeAlert={onChange} />
        {module !== 'surveyClassList' && (
          <>
            <Typography
              variant='inherit'
              className='font-bold capitalize'
              color='primary'
              align='right'
              component='p'
            >
              Subject
            </Typography>
            <div className='col-span-3'>
              <AutocompleteField
                id='subjectId'
                name='subjectId'
                value={form.subject || ''}
                onChange={(value) => onChange('subjectId', value)}
                groupBy={(option) => option?.curriculumId}
                options={subjects || []}
                placeholder='Choose an option'
                loading={isLoading}
              />
            </div>
          </>
        )}
        {module !== 'surveyClassList' && (
          <>
            <Typography
              variant='inherit'
              className='font-bold capitalize'
              color='primary'
              align='right'
              component='p'
            >
              Teachers
            </Typography>
            <div className='col-span-3'>
              <AutocompleteField
                id='form-filter-teachers'
                name='teachers'
                placeholder='Choose teachers'
                multiple={true}
                multiline={true}
                options={
                  teachers
                    ?.filter((item) => !form.teachersId?.includes(item._id))
                    .map((item) => ({
                      value: item._id,
                      label: `${item.code} - ${item.firstName}  ${item.lastName}`
                    })) || []
                }
                onChange={(value) => onChange('teachers', value)}
                value={form.teachers}
              />
            </div>
          </>
        )}
      </Box>
      {inputErrors.message && (
        <Typography variant='body2' color='error' align='center' className='mt-4'>
          {inputErrors.message}
        </Typography>
      )}
    </DialogConfirmCancel>
  )
}

export default FilterDialog
